import {
    Box,
    Card,
    IconButton,
    TextField,
    Tooltip,
    Typography,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Stack,
    Switch
} from "@mui/material";
import {
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon,
    Refresh,
} from '@mui/icons-material';
import { useState } from "react";
import { HelperView, HelperSkillAllOfFunctions } from "@my-helpers/common";

interface HelperSkillsProps {
    currentHelper?: HelperView;
    addSkills: (helperId: string, skills: string[]) => Promise<boolean>;
    removeSkills: (helperId: string, skills: string[]) => Promise<boolean>;
}

export const HelperSkills: React.FC<HelperSkillsProps> = (
    { currentHelper, addSkills, removeSkills }) => {
    
    const [skillsSearchTerm, setSkillsSearchTerm] = useState('');
    const [displayedToolGroups, setDisplayedToolGroups] = useState(5);

    const handleToolToggle = (
        toolId: string, 
        functions: HelperSkillAllOfFunctions[], checked: boolean) => {
       if (!currentHelper || !toolId) return;
       
       if (checked) {
           const ids = functions.filter(func => !func.helper_enabled).map(func => toolId + ":" + func.name);
           addSkills(currentHelper.id, ids);
       } else {
           const ids = functions.filter(func => func.helper_enabled).map(func => toolId + ":" + func.name);
           removeSkills(currentHelper.id, ids);
       }
    };

    const handleToolFunctionToggle = async (toolId: string, functionName: string, checked: boolean) => {
        if (!currentHelper || !toolId || !functionName) return;
    
        if (checked) {
            await addSkills(currentHelper.id, [toolId + ":" + functionName]);
        } else {
            await removeSkills(currentHelper.id, [toolId + ":" + functionName]);
        }
    };

    const handleLoadMoreSkills = () => {
        setDisplayedToolGroups(displayedToolGroups + 5);
    };
    
    return (
        <Card sx={{
            flex: 1,
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
        }}>
            <Box sx={{
                p: 3,
                pb: 2,
                flexShrink: 0
            }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2
                }}>
                    <Typography 
                        variant="h5" 
                        fontWeight="bold"
                        sx={{ 
                            color: 'primary.dark',
                        }}>
                        Skills
                        </Typography>
                </Box>
            </Box>

            <Box sx={{
                flex: 1,
                overflow: 'auto',
                px: 3,
                minHeight: 0
            }}>
                <Stack spacing={1}>
                    {currentHelper && currentHelper.skills.map((skill) => (
                        <Accordion 
                            key={skill.tool_id} 
                            sx={{
                                boxShadow: 'none',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                '&:before': {
                                    display: 'none',
                                },
                            }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    '& .MuiAccordionSummary-content': {
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    },
                                    '& .MuiAccordionSummary-content.Mui-expanded': {
                                        my: 0,
                                    }
                                }}
                            >
                                <Box>
                                    <Typography variant="subtitle2" sx={{
                                        fontWeight: 600
                                    }}>
                                        {skill.provider}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary" >
                                        {skill.description}
                                    </Typography>
                                </Box>
                                <Switch
                                    size="small"
                                    checked={skill.helper_enabled !== 'none'}
                                    onChange={(e) => handleToolToggle(skill.tool_id, skill.functions, e.target.checked)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </AccordionSummary>
                            <AccordionDetails>
                                <Stack spacing={1} sx={{ pr: 3 }}>
                                    {skill.functions.map((func) => (
                                        <Box
                                            key={skill.tool_id + func.name}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="body2">
                                                    {func.name}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary" >
                                                    {func.description}
                                                </Typography>
                                            </Box>
                                            <Switch
                                                size="small"
                                                checked={func.helper_enabled}
                                                onChange={(event) => handleToolFunctionToggle(skill.tool_id, func.name, event.target.checked)}
                                            />
                                        </Box>
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Stack>
            </Box>
        </Card>
    );
};